import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { BaseQLMiddleware } from '@adornis/baseql/metadata/types.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { Appointment } from './appointment.js';
import { Product } from './products.js';
import { Setting } from './settings.js';
import { Teaching } from './teaching.js';

type VersionDiffAppointment = import('../api/product.js').VersionDiffAppointment;
type VersionDiffProduct = import('../api/product.js').VersionDiffProduct;
type VersionDiffSetting = import('../api/product.js').VersionDiffSetting;
type VersionDiffTeachings = import('../api/product.js').VersionDiffTeachings;
type VersionDiffWorkshop = import('../api/product.js').VersionDiffWorkshop;

const versionAppointmentsMiddleware: BaseQLMiddleware = async ({ src, context, next }) => {
  const appointments = await next();
  const { mayViewWorkshopMeetingLinks, stripAppointmentMeetingLinks } = await import('../server/meeting-link-access.js');
  const product = (src as ProductUpdateWithReferences).product;
  return await mayViewWorkshopMeetingLinks(context, product ?? {})
    ? appointments
    : stripAppointmentMeetingLinks(appointments);
};

@Entity()
export class ProductUpdateWithReferences extends AdornisEntity {
  static override _class = 'ProductWithReferences';

  constructor(doc: PartialEntityData<ProductUpdateWithReferences>) {
    super(doc);
  }

  @Field(type => Product)
  product!: VersionDiffProduct | VersionDiffWorkshop;

  @Field(type => [Teaching])
  teachings?: VersionDiffTeachings[];

  @Field(type => [Appointment], { middlewares: [versionAppointmentsMiddleware] })
  appointments?: VersionDiffAppointment[];

  @Field(type => Setting)
  setting?: Maybe<VersionDiffSetting>;
}

// These selections are only needed as types; do not evaluate the product API here.
type ProductUpdateWithReferencesNarrSelSet = {
  readonly product: typeof import('../api/product.js').versionDiffProductSelSet;
  readonly teachings: typeof import('../api/product.js').versionDiffTeachingSelSet;
  readonly appointments: typeof import('../api/product.js').versionDiffAppointmentSelSet;
  readonly setting: typeof import('../api/product.js').versionDiffSettingSelSet;
};
export type ProductUpdateWithReferencesNarrEnt = NarrowedEntity<
  ProductUpdateWithReferences,
  ProductUpdateWithReferencesNarrSelSet
>;
